import type { Event } from '@sentry/react'

/**
 * Scrubs sensitive data from Sentry events to prevent PII leakage
 */
export function scrubSensitiveData(event: Event | null): Event | null {
  if (!event) return null

  // Remove Authorization headers
  if (event.request?.headers) {
    delete event.request.headers.Authorization
    delete event.request.headers['authorization']
    delete event.request.headers['x-api-key']
  }

  // Strip sensitive query parameters from URL
  if (event.request?.url) {
    try {
      const url = new URL(event.request.url)
      const sensitiveParams = [
        'token',
        'auth',
        'password',
        'secret',
        'key',
        'api_key',
        'access_token',
        'refresh_token',
        'email',
        'phone',
        'ssn',
        'credit_card',
      ]

      sensitiveParams.forEach(param => {
        url.searchParams.delete(param)
      })

      event.request.url = url.toString()
    } catch {
      // If URL parsing fails, try to remove common patterns
      event.request.url = event.request.url.replace(
        /[?&](token|auth|password|secret|key|api_key|access_token|refresh_token|email|phone)=[^&]*/gi,
        '',
      )
    }
  }

  // Remove form data values
  if (event.request?.data) {
    if (
      typeof event.request.data === 'object' &&
      event.request.data !== null &&
      !Array.isArray(event.request.data)
    ) {
      const data = event.request.data as Record<string, unknown>
      const sensitiveFields = [
        'password',
        'email',
        'phone',
        'token',
        'secret',
        'apiKey',
        'creditCard',
        'ssn',
      ]

      sensitiveFields.forEach(field => {
        if (field in data) {
          delete data[field]
        }
      })
    }
  }

  // Scrub error messages that might contain sensitive data
  if (event.message) {
    event.message = scrubString(event.message)
  }

  // Scrub exception values
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map(exception => {
      if (exception.value) {
        exception.value = scrubString(exception.value)
      }
      if (exception.stacktrace?.frames) {
        exception.stacktrace.frames = exception.stacktrace.frames.map(frame => {
          if (frame.vars) {
            frame.vars = scrubObject(frame.vars)
          }

          return frame
        })
      }

      return exception
    })
  }

  // Scrub breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
      if (breadcrumb.data) {
        breadcrumb.data = scrubObject(breadcrumb.data)
      }
      if (breadcrumb.message) {
        breadcrumb.message = scrubString(breadcrumb.message)
      }

      return breadcrumb
    })
  }

  // Remove localStorage/sessionStorage contents from contexts
  if (event.contexts) {
    delete event.contexts.localStorage
    delete event.contexts.sessionStorage
  }

  return event
}

/**
 * Scrubs sensitive patterns from strings
 */
function scrubString(str: string): string {
  // Remove email addresses
  str = str.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')

  // Remove phone numbers
  str = str.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]')

  // Remove tokens (long alphanumeric strings)
  str = str.replace(/\b[a-zA-Z0-9]{32,}\b/g, '[token]')

  // Remove file paths that might contain sensitive info
  str = str.replace(/\/[^\s]+\/([^/\s]+\.(txt|doc|pdf|jpg|png|gif|mp4|mov))/gi, '[file]')

  return str
}

/**
 * Recursively scrubs sensitive data from objects
 */
function scrubObject(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return typeof obj === 'string' ? scrubString(obj) : obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => scrubObject(item))
  }

  const scrubbed: any = {}

  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase()

    // Skip sensitive keys
    if (
      lowerKey.includes('password') ||
      lowerKey.includes('token') ||
      lowerKey.includes('secret') ||
      lowerKey.includes('key') ||
      lowerKey.includes('email') ||
      lowerKey.includes('phone') ||
      lowerKey.includes('credit') ||
      lowerKey.includes('ssn') ||
      lowerKey.includes('auth')
    ) {
      scrubbed[key] = '[redacted]'
    } else if (typeof value === 'string') {
      scrubbed[key] = scrubString(value)
    } else if (typeof value === 'object' && value !== null) {
      scrubbed[key] = scrubObject(value)
    } else {
      scrubbed[key] = value
    }
  }

  return scrubbed
}
